import React from 'react';
import { Car, MapPin, Clock, Users } from 'lucide-react';
import { StorytellingLayout, ImageCarousel, ExpandableCard } from '../base';

/**
 * Tela de storytelling para transfers
 * Responsabilidade: Exibir informações de transfer (ida OU volta)
 * 
 * Props:
 * - block: dados do bloco de transfer
 * - isOutbound: true = transfer de ida (aeroporto → hotel), false = volta
 * - stepIndicator: "Passo X de Y"
 * - narrativeText: texto emocional
 * - ctaText: texto do botão
 */
export default function StorytellingTransfer({
  block,
  isOutbound = true,
  stepIndicator,
  narrativeText,
  ctaText,
  onContinue,
  onBack,
  onShare
}) {
  const details = block.details || {};
  
  // Imagens padrão para transfer
  const defaultImages = [
    'https://dicasdaitalia.com.br/wp-content/uploads/sites/11/2017/10/servico-transfer-italia.jpg',
    'https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=800&q=80'
  ];
  const images = block.images || defaultImages;

  // Monta título
  const title = isOutbound 
    ? `Transfer ${details.origin_name || 'Aeroporto'} → ${details.destination_name || 'Hotel'}`
    : `Transfer ${details.origin_name || 'Hotel'} → ${details.destination_name || 'Aeroporto'}`;

  // Formata data
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('pt-BR');
  };

  const cardContent = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {/* Header: Ícone + Tipo + Data */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
        <div style={{
          width: '48px',
          height: '48px',
          backgroundColor: 'rgba(93, 212, 195, 0.15)',
          borderRadius: '0.75rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}>
          <Car size={24} color="#5DD4C3" strokeWidth={2} />
        </div>
        <div style={{ flex: 1 }}>
          <p style={{ fontSize: '0.8125rem', color: 'rgba(255, 255, 255, 0.6)', margin: 0 }}>
            Tipo de veículo
          </p>
          <p style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'white', margin: 0 }}>
            {details.vehicle_type || 'Sedan Executivo'}
          </p>
        </div>
        <div style={{ textAlign: 'right' }}>
          <p style={{ fontSize: '0.8125rem', color: 'rgba(255, 255, 255, 0.6)', margin: '0 0 0.25rem 0' }}>
            Data
          </p>
          <p style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'white', margin: 0 }}>
            {formatDate(block.date)}
          </p>
        </div>
      </div>

      {/* Rota */}
      <div style={{ paddingTop: '0.75rem', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {/* Origem */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
            <MapPin size={18} color="#5DD4C3" strokeWidth={2} fill="#5DD4C3" style={{ marginTop: '0.125rem' }} />
            <div style={{ flex: 1 }}>
              <p style={{ fontSize: '0.8125rem', color: 'rgba(255, 255, 255, 0.6)', margin: 0 }}>
                Partida
              </p>
              <p style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'white', margin: 0, lineHeight: 1.4 }}>
                {details.origin_name || 'Aeroporto'}
              </p>
              <p style={{ fontSize: '0.8125rem', color: 'rgba(255, 255, 255, 0.7)', margin: '0.125rem 0 0 0' }}>
                {details.pickup_time || '--:--'} {details.origin_details ? `• ${details.origin_details}` : ''}
              </p>
            </div>
          </div>

          {/* Linha conectora */}
          <div style={{ marginLeft: '0.5rem', borderLeft: '2px dashed rgba(255, 255, 255, 0.3)', height: '24px' }} />

          {/* Destino */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
            <MapPin size={18} color="rgba(255, 255, 255, 0.4)" strokeWidth={2} style={{ marginTop: '0.125rem' }} />
            <div style={{ flex: 1 }}>
              <p style={{ fontSize: '0.8125rem', color: 'rgba(255, 255, 255, 0.6)', margin: 0 }}>
                Destino
              </p>
              <p style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'white', margin: 0, lineHeight: 1.4 }}>
                {details.destination_name || 'Hotel'}
              </p>
              <p style={{ fontSize: '0.8125rem', color: 'rgba(255, 255, 255, 0.7)', margin: '0.125rem 0 0 0' }}>
                {details.destination_address || ''}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Informações rápidas */}
      <div style={{ paddingTop: '0.75rem', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Clock size={16} color="#5DD4C3" strokeWidth={2} />
            <div>
              <p style={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.6)', margin: 0 }}>Duração</p>
              <p style={{ fontSize: '0.875rem', fontWeight: 600, color: 'white', margin: 0 }}>
                ~{details.duration || '45 min'}
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Users size={16} color="#5DD4C3" strokeWidth={2} />
            <div>
              <p style={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.6)', margin: 0 }}>Passageiros</p>
              <p style={{ fontSize: '0.875rem', fontWeight: 600, color: 'white', margin: 0 }}>
                Até {details.max_passengers || 3}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const expandedContent = (
    <p style={{
      fontSize: '0.8125rem',
      color: 'rgba(255, 255, 255, 0.7)',
      lineHeight: 1.6,
      margin: 0
    }}>
      • Motorista bilíngue aguardando com placa<br/>
      • Veículo climatizado com Wi-Fi<br/>
      • {details.luggage_capacity || '2 malas grandes + 2 bagagens de mão'}<br/>
      • Água mineral cortesia<br/>
      • Cancelamento grátis até 24h antes
    </p>
  );

  return (
    <StorytellingLayout
      backgroundImage={images[0]}
      title={title}
      narrativeText={narrativeText}
      stepIndicator={stepIndicator}
      ctaText={ctaText}
      onContinue={onContinue}
      onBack={onBack}
      onShare={onShare}
    >
      <ImageCarousel images={images} />
      <ExpandableCard expandedContent={expandedContent}>
        {cardContent}
      </ExpandableCard>
    </StorytellingLayout>
  );
}
